import type { Sourcepos } from "../../types/node";
import type { AutolinkParser } from "../../types/parser";
import { ENTITY } from "../common";
import { createNode, text } from "../node";
import type NodeWalker from "../nodeWalker";

const DOMAIN = "(?:[w-]+.)*[A-Za-z0-9-]+.[A-Za-z0-9-]+";
// Stricter domain for bare (scheme-less) autolinks: requires literal dots and
// a 2+ letter TLD so plain text like "package.json" or "1.5.0" won't match.
const BARE_DOMAIN = "(?:[A-Za-z0-9-]+\\.)+[A-Za-z]{2,}";
const PATH = "[^<\\s]*[^<?!.,:*_?~\\s]";
const reEmailLocalChar = /[\w.+-]/;
const reEmailDomain = /(?:[\w-]+\.)+[\w-]+/y;

function trimUnmatchedTrailingParens(source: string) {
  const trailingParen = /\)+$/.exec(source);
  if (trailingParen) {
    let count = 0;
    for (const ch of source) {
      if (ch === "(") {
        if (count < 0) {
          count = 1;
        } else {
          count += 1;
        }
      } else if (ch === ")") {
        count -= 1;
      }
    }

    if (count < 0) {
      const trimCount = Math.min(-count, trailingParen[0].length);
      return source.substring(0, source.length - trimCount);
    }
  }
  return source;
}

function trimTrailingEntity(source: string) {
  return source.replace(/&[A-Za-z0-9]+;$/, "");
}

interface LinkInfo {
  text: string;
  url: string;
  range: [number, number];
}

/**
 * Scans from each "@" rather than running the whole address as one global regex, which retries
 * every start inside a run of local-part characters and goes quadratic on a long line of dashes or
 * digits. The matches are the same: a local part is always the whole run before its "@" (a shorter
 * one doesn't reach it), cut where the previous match ended.
 */
export function parseEmailLink(source: string) {
  const result: LinkInfo[] = [];
  for (let from = 0, at = source.indexOf("@"); at !== -1; at = source.indexOf("@", from)) {
    let start = at;
    while (start > from && reEmailLocalChar.test(source[start - 1]!)) {
      start--;
    }
    reEmailDomain.lastIndex = at + 1;
    if (start === at || !reEmailDomain.test(source)) {
      from = at + 1;
      continue;
    }
    from = reEmailDomain.lastIndex;
    const text = source.slice(start, from);
    if (!/[_-]$/.test(text)) {
      result.push({
        text,
        range: [start, from - 1],
        url: `mailto:${text}`,
      });
    }
  }

  return result;
}

export function parseUrlLink(source: string) {
  const reWwwAutolink = new RegExp(`(www|https?://)\.${DOMAIN}${PATH}`, "g");
  // Bare domain+path URLs (e.g. "github.com/marp-team/marp"). Requires a `/path`
  // to avoid false positives like "e.g." or "package.json", and a lookbehind to
  // avoid re-matching inside already-schemed URLs, file paths, or email domains.
  const reBareAutolink = new RegExp(`(?<![A-Za-z0-9./@+_-])${BARE_DOMAIN}/${PATH}`, "g");
  const result: LinkInfo[] = [];
  let m;

  while ((m = reWwwAutolink.exec(source))) {
    const text = trimTrailingEntity(trimUnmatchedTrailingParens(m[0]));
    const scheme = m[1] === "www" ? "http://" : "";
    result.push({
      text,
      range: [m.index, m.index + text.length - 1],
      url: `${scheme}${text}`,
    });
  }

  while ((m = reBareAutolink.exec(source))) {
    const text = trimTrailingEntity(trimUnmatchedTrailingParens(m[0]));
    const start = m.index;
    const end = start + text.length - 1;
    if (result.some((r) => start <= r.range[1] && end >= r.range[0])) continue;
    result.push({
      text,
      range: [start, end],
      url: `https://${text}`,
    });
  }

  result.sort((a, b) => a.range[0] - b.range[0]);

  return result;
}

const reNotAnyChar = /[\n\r]/;
const reDecoded = new RegExp(`\\\\|${ENTITY}`, "i");

/**
 * Whether the character at `at` could fall inside a www/http autolink, so text split there would
 * autolink differently from the whole. Only the pattern's any-character positions — the dots the
 * scheme and DOMAIN leave unescaped — can take whitespace: the one after the scheme, one after each
 * "w"/"-" run, and one after the label that starts the final domain part.
 *
 * Reads source text, which matches the text node's literal only where no entity or backslash escape
 * was decoded, so one within reach answers yes.
 */
export function mayAutolinkAcross(text: string, at: number) {
  let lo = at;
  const memo = new Map<number, boolean>();
  // Whether `p` can be the any-character after the scheme or after a "w"/"-" run.
  const wildcard = (p: number): boolean => {
    if (p < 0 || reNotAnyChar.test(text[p]!)) {
      return false;
    }
    let hit = memo.get(p);
    if (hit === undefined) {
      lo = Math.min(lo, p - 8);
      hit = /(?:www|https?:\/\/)$/.test(text.slice(Math.max(0, p - 8), p)) || runBefore(p, /[w-]/);
      memo.set(p, hit);
    }
    return hit;
  };
  // Whether a run of `char` ends right before `p` and starts right after a wildcard.
  const runBefore = (p: number, char: RegExp) => {
    for (let q = p - 1; q >= 0 && char.test(text[q]!); q--) {
      // The walk recurses once per character; past this reach, yes is the safe answer.
      if (at - q > 256) {
        return true;
      }
      lo = Math.min(lo, q);
      if (wildcard(q - 1)) {
        return true;
      }
    }
    return false;
  };
  return wildcard(at) || runBefore(at, /[A-Za-z0-9-]/) || reDecoded.test(text.slice(Math.max(0, lo - 40), at));
}

function baseAutolinkParser(source: string) {
  return [...parseUrlLink(source), ...parseEmailLink(source)].sort((a, b) => a.range[0] - b.range[0]);
}

export function convertExtAutoLinks(walker: NodeWalker, autolinkParser: boolean | AutolinkParser) {
  if (typeof autolinkParser === "boolean") {
    autolinkParser = baseAutolinkParser;
  }

  let event;
  while ((event = walker.next())) {
    const { entering, node } = event;
    if (entering && node.type === "text" && node.parent!.type !== "link") {
      const literal = node.literal!;
      const linkInfos = autolinkParser(literal);

      if (!linkInfos || !linkInfos.length) {
        continue;
      }

      let lastIdx = 0;
      const [lineNum, chPos] = node.sourcepos![0];
      const sourcepos = (startIdx: number, endIdx: number): Sourcepos => [
        [lineNum, chPos + startIdx],
        [lineNum, chPos + endIdx],
      ];
      const newNodes = [];
      for (const { range, url, text: linkText } of linkInfos) {
        if (range[0] > lastIdx) {
          newNodes.push(text(literal.substring(lastIdx, range[0]), sourcepos(lastIdx, range[0] - 1)));
        }
        const linkNode = createNode("link", sourcepos(...range));
        linkNode.appendChild(text(linkText, sourcepos(...range)));
        linkNode.destination = url;
        newNodes.push(linkNode);
        lastIdx = range[1] + 1;
      }
      if (lastIdx < literal.length) {
        newNodes.push(text(literal.substring(lastIdx), sourcepos(lastIdx, literal.length - 1)));
      }

      for (const newNode of newNodes) {
        node.insertBefore(newNode);
      }
      node.unlink();
    }
  }
}
